/**
 * Full-screen barcode scanner modal.
 *
 * Built on @zxing/browser's BrowserMultiFormatReader, which supports all the
 * 1D + 2D formats Spire customers typically print: Code 128, EAN-13, UPC-A/E,
 * QR, Data Matrix. The PWA must run over HTTPS or localhost for getUserMedia
 * to grant access; on iOS Safari, the user has to tap "Allow Camera" once per
 * origin.
 *
 * Lifecycle:
 *   - On open: enumerate cameras, pick the rear (environment) one when
 *     available, start continuous decoding.
 *   - On first successful decode: invoke onScan, then close.
 *   - On error or unmount: stop the underlying MediaStream so the camera
 *     light goes off.
 */

import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import type { Result } from '@zxing/library';
import { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button.js';
import { useDialogA11y } from '../lib/use-dialog-a11y.js';

interface ScanModalProps {
  open: boolean;
  onScan: (decoded: { text: string; format: string }) => void;
  onClose: () => void;
}

type Phase = 'requesting' | 'scanning' | 'denied' | 'unavailable' | 'error';

export function ScanModal({ open, onScan, onClose }: ScanModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  // Stash the latest onScan in a ref so the camera-lifecycle effect can
  // depend ONLY on `open`. Otherwise an inline-function onScan from the
  // parent re-creates on every render → effect re-runs → camera flashes
  // on/off and never decodes a barcode.
  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const [phase, setPhase] = useState<Phase>('requesting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Self-gating dialog (`if (!open) return null` below) — pass `open` as
  // `enabled` and keep the hook call above the early return.
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose, open);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();
    setPhase('requesting');
    setErrorMessage(null);

    (async () => {
      try {
        const videoEl = videoRef.current;
        if (!videoEl) return;
        if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
          if (!cancelled) {
            setPhase('unavailable');
            setErrorMessage('Camera API not supported in this browser.');
          }
          return;
        }

        // Use facingMode constraints — the browser picks the rear camera
        // natively. The earlier deviceId-enumeration flow misfired on iOS
        // Safari because labels are blank before permission is granted and
        // the back-camera regex fell through to the front camera.
        const constraints: MediaStreamConstraints = {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };
        const controls = await reader.decodeFromConstraints(
          constraints,
          videoEl,
          (result: Result | undefined, err) => {
            if (cancelled) return;
            if (result) {
              const text = result.getText();
              const format = String(result.getBarcodeFormat());
              controls.stop();
              controlsRef.current = null;
              onScanRef.current({ text, format });
            } else if (err && err.name !== 'NotFoundException') {
              setErrorMessage(err.message);
            }
          },
        );
        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
        setPhase('scanning');
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        const isPermissionError =
          err instanceof DOMException &&
          (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
        const isNoCamera =
          err instanceof DOMException &&
          (err.name === 'NotFoundError' || err.name === 'OverconstrainedError');
        if (isPermissionError) {
          setPhase('denied');
        } else if (isNoCamera) {
          setPhase('unavailable');
          setErrorMessage('No camera detected on this device.');
        } else {
          setPhase('error');
          setErrorMessage(message);
        }
      }
    })();

    return () => {
      cancelled = true;
      const controls = controlsRef.current;
      if (controls) {
        controls.stop();
        controlsRef.current = null;
      }
    };
    // Intentionally only `open` — onScan is read via ref above so a
    // parent re-render can't restart the camera mid-decode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Scan a barcode"
      className="fixed inset-0 z-50 bg-black/90 text-white flex flex-col"
    >
      <header className="flex items-center justify-between p-4">
        <div className="text-sm font-medium">Scan barcode</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close scanner"
          className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
        >
          ✕
        </button>
      </header>

      <div className="flex-1 flex items-center justify-center">
        <div className="relative w-full max-w-md aspect-[3/4] bg-black">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            muted
            playsInline
          />
          {/* Reticle overlay */}
          <div className="pointer-events-none absolute inset-x-6 top-1/4 bottom-1/4 border-2 border-white/70 rounded-lg" />
        </div>
      </div>

      <footer className="p-4 text-center text-sm space-y-2">
        {phase === 'requesting' && <div>Requesting camera…</div>}
        {phase === 'scanning' && <div>Point the camera at a barcode.</div>}
        {phase === 'denied' && (
          <div className="space-y-2">
            <div className="text-red-300">Camera access denied.</div>
            <div className="text-xs text-white/70">
              Open your browser's site settings to grant camera permission, then try again.
            </div>
          </div>
        )}
        {phase === 'unavailable' && (
          <div className="text-amber-200">{errorMessage ?? 'Camera unavailable.'}</div>
        )}
        {phase === 'error' && errorMessage && (
          <div className="text-red-300">{errorMessage}</div>
        )}
        <div>
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </footer>
    </div>
  );
}
